import React, { useEffect, useState, useRef } from "react";
import { ScrollArea, useToast } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { Header } from "../../components/modules";
import { FiFileText } from "react-icons/fi";
import Context from "../../Context";
import { MdPersonSearch, MdAccessTime } from "react-icons/md";
import { Spinner } from "reactstrap";
import { CSVLink } from "react-csv";
import { thaiDate } from "../../util";
import constant from "../../constants";

export const ReportDailySale = () => {
  const downloadRef = useRef(null);
  const { callApi, configs, db } = Context();
  const [selectedRoundDate, setSelectedRoundDate] = useState(configs.roundDate);
  const [loading, setLoading] = useState(false);
  const [sales, setSale] = useState(false);

  // Hourly Analysis States
  const getTodayDateICT = () => {
    const d = new Date();
    // Offset local timezone to ICT (UTC+7)
    const utc = d.getTime() + d.getTimezoneOffset() * 60000;
    const ictDate = new Date(utc + 3600000 * 7);
    return ictDate.toISOString().slice(0, 10);
  };

  const [hourlyDate, setHourlyDate] = useState(getTodayDateICT());
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyStats, setHourlyStats] = useState(null);
  const [peakHour, setPeakHour] = useState(null);

  const [csvFileName, setCsvFileName] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [downloaded, setDownloaded] = useState([]);

  useEffect(() => {
    if (csvFileName && !downloaded.includes(csvFileName)) {
      downloadRef.current.childNodes[0].click();
      setDownloaded((old) => [...old, csvFileName]);
    }
  }, [csvFileName]);

  const getUsers = async () => {
    setLoading(true);

    let { data } = await callApi(
      "dashboard-getDashboard",
      {
        roundDate: selectedRoundDate,
      },
      {
        notShowLoading: true,
      }
    );

    data.historySale = Object.entries(data?.historySale || {})
      .map(([key, value]) => {
        return {
          date: key,
          size: value.size,
          income: value.income,
          orderCount: value.orderCount,
        };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    data.historyUser = Object.entries(data.historyNewUser || {})
      .map(([key, value]) => {
        return { date: key, size: value.size };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    data.totalIncome = Object.values(data?.historySale).reduce((acc, value) => {
      acc += value.income;
      return acc;
    }, 0);

    data.roundDateNewUser = Object.values(data.historyNewUser || {}).reduce(
      (acc, value) => {
        acc += Number(value.size);
        return acc;
      },
      0
    );

    setSale(data);
    setLoading(false);
  };

  const getHourlySales = async () => {
    if (!hourlyDate) return;
    setHourlyLoading(true);
    setHourlyStats(null);
    setPeakHour(null);

    try {
      const q = query(
        collection(db, "Orders"),
        where("createdAtDate", "==", hourlyDate),
        where("status", "==", "approved")
      );
      const querySnapshot = await getDocs(q);

      const hourlyData = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        label: `${String(i).padStart(2, "0")}:00 - ${String(i + 1).padStart(2, "0")}:00 น.`,
        orderCount: 0,
        ticketSize: 0,
        income: 0,
      }));

      let totalOrders = 0;
      let totalTickets = 0;
      let totalIncome = 0;

      querySnapshot.forEach((doc) => {
        const order = doc.data();
        // Parse time from createdAt timestamp
        const dateObj = new Date(order.createdAt);
        const hour = dateObj.getHours();

        if (hour >= 0 && hour < 24) {
          hourlyData[hour].orderCount += 1;
          hourlyData[hour].ticketSize += Number(order.size || 0);
          hourlyData[hour].income += Number(order.totalPrice || 0);

          totalOrders += 1;
          totalTickets += Number(order.size || 0);
          totalIncome += Number(order.totalPrice || 0);
        }
      });

      // Find hour with maximum income
      let maxIncome = -1;
      let peak = null;
      hourlyData.forEach((h) => {
        if (h.income > maxIncome && h.income > 0) {
          maxIncome = h.income;
          peak = h;
        }
      });

      setHourlyStats({
        hours: hourlyData,
        totalOrders,
        totalTickets,
        totalIncome,
      });
      setPeakHour(peak);
    } catch (err) {
      console.error("Failed to fetch hourly sales:", err);
      window.alert("เกิดข้อผิดพลาดในการดึงข้อมูลรายชั่วโมง");
    } finally {
      setHourlyLoading(false);
    }
  };

  const handleDownload = async (type) => {
    const csvBody = [];

    csvBody.push([
      "วัน",
      "คำสั่งซื้อทั้งหมด (ออเดอร์)",
      "ยอดขายสลาก (ใบ)",
      "ยอดขาย (บาท)",
    ]);

    sales.historySale.forEach((sale, index) =>
      csvBody.push([
        sale.date,
        sale.orderCount.toLocaleString(),
        sale.size.toLocaleString(),
        sale.income.toLocaleString(),
      ])
    );

    setCsvFileName(`รายงานยอดขายรายวัน_งวด_${selectedRoundDate}.csv`);

    setCsvData(csvBody);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mb-24">
      {/* header */}
      <Header
        header="Report"
        subHeader="รายงานยอดขายรายวัน"
        icon={<FiFileText className="text-blue" />}
      />
      {/* Hourly Sales Behavior Analysis Card */}
      <div className="relative w-full bg-white rounded-lg shadow-md mt-4 border border-[#E5E7E9] overflow-hidden">
        <div className="p-3 border-b border-[#E5E7E9] flex items-center gap-2">
          <MdAccessTime className="text-blue text-xl" />
          <p className="font-semibold">คำสั่งซื้อรายวัน </p>
        </div>
        <div className="p-3">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className=" ">
              <p className="font-medium text-xs mb-1">วันที่</p>
              <input
                type="date"
                className="border  py-1 px-2 border-[#1a1b1c] rounded-lg font-semibold cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
                value={hourlyDate}
                disabled={hourlyLoading}
                onChange={(e) => setHourlyDate(e.target.value)}
              />
            </div>
            <button
              disabled={hourlyLoading || !hourlyDate}
              className="flex items-center justify-center gap-1 py-1.5 px-3 bg-blue text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-60 h-[22px]"
              onClick={() => {
                getHourlySales();
              }}
            >
              {hourlyLoading ? (
                <Spinner className="w-3 h-3" />
              ) : (
                <MdPersonSearch className="text-white" />
              )}
              ค้นหา
            </button>
          </div>

          {peakHour && (
            <div className="mt-4 p-3 bg-[#e8f5e9] border border-[#a5d6a7] text-[#2e7d32] rounded-lg text-sm font-semibold flex items-center gap-2">
              <span>🔥</span>
              <span>
                ช่วงเวลาที่ขายดีที่สุดในวันนี้: <strong>{peakHour.label}</strong> ยอดขายรวม <strong>{peakHour.income.toLocaleString()} บาท</strong> ({peakHour.ticketSize.toLocaleString()} ใบ, {peakHour.orderCount.toLocaleString()} ออเดอร์)
              </span>
            </div>
          )}

          {hourlyStats && (
            <div className="mt-4 border border-[#E5E7E9] rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-[#f2f3f5]">
                  <TableRow>
                    <TableHead className="">ช่วงเวลา</TableHead>
                    <TableHead className="text-center">จำนวนออเดอร์ (ออเดอร์)</TableHead>
                    <TableHead className="text-center">ยอดขายสลาก (ใบ)</TableHead>
                    <TableHead className="text-center">ยอดขาย (บาท)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hourlyStats.hours.map((h) => {
                    const isPeak = peakHour && peakHour.hour === h.hour;
                    return (
                      <TableRow
                        key={h.hour}
                        className={`${
                          h.orderCount > 0 ? "cursor-pointer" : ""
                        } transition-colors duration-150 ${
                          isPeak
                            ? "bg-[#fffbeb] font-bold border-l-4 border-amber-500 hover:bg-[#fff7cf]"
                            : h.orderCount > 0
                            ? "hover:bg-slate-50"
                            : ""
                        }`}
                        onClick={() => {
                          if (h.orderCount > 0) {
                            window.open(`/reports/daily/hourly?date=${hourlyDate}&hour=${h.hour}`, "_blank");
                          }
                        }}
                      >
                        <TableCell className="font-medium">
                          {h.label} {isPeak && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Peak Hour</span>}
                        </TableCell>
                        <TableCell className="text-center">{h.orderCount.toLocaleString()}</TableCell>
                        <TableCell className="text-center">{h.ticketSize.toLocaleString()}</TableCell>
                        <TableCell className="text-center text-green font-semibold">
                          {h.income.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-[#f2f3f5] font-bold">
                    <TableCell>รวมทั้งสิ้น</TableCell>
                    <TableCell className="text-center">{hourlyStats.totalOrders.toLocaleString()}</TableCell>
                    <TableCell className="text-center">{hourlyStats.totalTickets.toLocaleString()}</TableCell>
                    <TableCell className="text-center text-green">{hourlyStats.totalIncome.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {sales && (
        <div className="relative min-w-96 bg-white w-full rounded-lg shadow-md mt-4 border border-[#E5E7E9] overflow-hidden">
          <div className="flex bg-white gap-3 items-center p-3 border-b border-[#E5E7E9]">
            <p className="font-semibold ">รายงานยอดขายรายวัน</p>
            <p className="text-xs font-semibold text-[#969797]">|</p>
            <p className="text-xs font-semibold text-[#969797]">
              รวม {sales.totalIncome.toLocaleString()} บาท
            </p>

            <button
              disabled={!sales}
              className="flex items-center justify-center gap-1 py-2 px-3 bg-[rgb(59,176,101)] text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:bg-slate-300 disabled:hover:opacity-100 disabled:cursor-not-allowed"
              onClick={() => {
                handleDownload("orders");
              }}
            >
              {loading ? <Spinner className="w-3 h-3" /> : "ดาวโหลด"}
            </button>
          </div>
          {/* <ScrollArea className={`h-96`}> */}
          <Table>
            <TableHeader className={`bg-[#f2f3f5]`}>
              <TableRow>
                <TableHead className="">วันที่</TableHead>
                <TableHead className={`text-center`}>
                  คำสั่งซื้อร์ทั้งหมด (ออเดอร์)
                </TableHead>
                <TableHead className={`text-center`}>ยอดขายสลาก (ใบ)</TableHead>
                <TableHead className={`text-center`}>ยอดขาย (บาท)</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className={``}>
              {sales.historySale.length > 0 ? (
                <>
                  {sales.historySale.map((sale, index) => {
                    return (
                      <TableRow key={sale.date}>
                        <TableCell className="font-medium">{sale.date}</TableCell>
                        <TableCell className={`font-medium text-center`}>
                          {sale.orderCount?.toLocaleString()}
                        </TableCell>
                        <TableCell className={`font-medium text-center`}>
                          {sale.size?.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-medium text-center">
                          {sale.income?.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-[#f2f3f5] font-bold">
                    <TableCell className="font-bold">รวม</TableCell>
                    <TableCell className="font-bold text-center">
                      {sales.historySale.reduce((acc, sale) => acc + (sale.orderCount || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-bold text-center">
                      {sales.historySale.reduce((acc, sale) => acc + (sale.size || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-bold text-center text-green">
                      {sales.historySale.reduce((acc, sale) => acc + (sale.income || 0), 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell className="" colSpan="8">
                    <div className="min-h-52 w-full flex justify-center items-center font-medium text-[#969797]">
                      <div>
                        <MdPersonSearch className="text-white" />
                        ไม่พบคำสั่งซื้อ
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* </ScrollArea> */}
        </div>
      )}
      <div className="relative w-96 bg-white rounded-lg shadow-md mt-4 border border-[#E5E7E9] overflow-hidden">
        <div className="p-3 border-b border-[#E5E7E9]">
          <p className="font-semibold ">ค้นหารายงานยอดขายรายวัน </p>
        </div>
        <div className="p-3 ">
          <p className="font-medium text-xs">งวด</p>
          <div>
            <select
              className="border w-full py-1 border-[#1a1b1c] rounded-lg font-semibold cursor-pointer disabled:cursor-not-allowed disabled:opacity-65"
              value={selectedRoundDate}
              onChange={(e) => setSelectedRoundDate(e.target.value)}
            >
              {configs.roundDates.map((roundDate) => (
                <option key={roundDate} value={roundDate}>
                  {roundDate}
                </option>
              ))}
            </select>
          </div>
          <div className="flex mt-3">
            <button
              disabled={loading}
              className="flex w-full items-center justify-center gap-1 py-2 px-3 bg-blue text-white text-sm font-semibold rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-60"
              onClick={() => {
                getUsers();
              }}
            >
              {loading ? (
                <Spinner className="w-3 h-3" />
              ) : (
                <MdPersonSearch className="text-white" />
              )}
              ค้นหา
            </button>
          </div>

          <div ref={downloadRef} className="hidden">
            <CSVLink
              data={csvData}
              filename={csvFileName}
              style={{ display: "none" }}
            >
              Download me
            </CSVLink>
          </div>
        </div>
      </div>


    </div>
  );
};
